import useQueryParams from 'hooks/useQueryParams'
import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setActiveItem } from './PatientSidebar/patientSidebarSlice'
import { PatientSidebarItemKeys } from './PatientSidebar/patientSidebarConsts'
import useFeatureFlags from 'hooks/useFeatureFlags'
import usePlanLimits from 'hooks/usePlanLimits'
import useRolePermissions from 'hooks/useRolePermissions'

export default () => {
  const dispatch = useDispatch()
  const { setQueryParams, queryParams } = useQueryParams()
  const { copilotControl: copilotFF, liveCalls: liveCallsFF } = useFeatureFlags()
  const { isGP, isHI } = usePlanLimits()
  const { isHiUser } = useRolePermissions()
  const appSettings = useSelector(s => s.profileReducer?.doctor?.user?.appSettings)
  // Used as a "user data loaded" signal — gates the no-requestedView path
  // (where we populate the URL with the saved or default view) so we don't
  // pick a wrong default while isHI / copilotFF are still null on initial
  // render. Note: doctor.id loads BEFORE billing.grinPlan in some sessions,
  // so this alone is NOT enough to safely rewrite an existing patientView
  // value — see the KNOWN_VIEWS check below for that case.
  const doctorId = useSelector(s => s.profileReducer?.doctor?.id)

  const defaultPatientView = useMemo(() => {
    if (isHiUser) {
      return PatientSidebarItemKeys.Chat
    }
    if (isGP) {
      return PatientSidebarItemKeys.Notes
    }
    if (isHI && copilotFF) {
      return PatientSidebarItemKeys.Copilot
    }
    return PatientSidebarItemKeys.Chat
  }, [isHiUser, isGP, isHI, copilotFF])

  const savedView = useMemo(() => {
    try {
      return JSON.parse(appSettings || '{}')?.defaultPatientView
    } catch (e) {
      return undefined
    }
  }, [appSettings])

  const allowedViews = useMemo(() => {
    const baseViews = (() => {
      if (isGP) {
        return ['notes', 'chat']
      }
      if (isHI && copilotFF) {
        return ['copilot', 'chat', 'notes']
      }
      return ['chat', 'notes']
    })()

    // Add liveCalls if the feature flag is enabled
    if (liveCallsFF) {
      return [...baseViews, 'liveCalls']
    }

    return baseViews
  }, [isGP, isHI, copilotFF, liveCallsFF])

  useEffect(() => {
    const allowedSavedView = savedView && allowedViews.includes(savedView) ? savedView : undefined
    const fallbackView = allowedSavedView || defaultPatientView
    const requestedView = queryParams.patientView

    if (requestedView) {
      if (allowedViews.includes(requestedView)) {
        dispatch(setActiveItem(requestedView))
        return
      }

      // requestedView isn't (yet) in allowedViews. Two reasons this can happen:
      //   (a) the view is genuinely unknown — e.g. `?patientView=garbage`
      //   (b) user data is still loading — `doctor.id` and `billing.grinPlan`
      //       arrive in separate Redux dispatches, so isHI/copilotFF can be
      //       false-ish for one or more render ticks even on a fresh hard
      //       reload from an HI doctor.
      // For (a) we want to rewrite to the fallback. For (b) we want to wait
      // and let allowedViews catch up. KNOWN_VIEWS distinguishes: a name
      // matching one of them is "potentially valid for some user type", so
      // give it the benefit of the doubt and don't rewrite — eventually
      // allowedViews populates and the dispatch fires above. A name that
      // isn't in KNOWN_VIEWS can never become valid, so rewrite immediately.
      if (KNOWN_VIEWS.includes(requestedView)) {
        return
      }

      setQueryParams({
        ...queryParams,
        patientView: fallbackView
      })
      return
    }

    // No requestedView on the URL — populate it with the saved or default
    // view. This branch DOES need to wait for user data, otherwise we'd
    // pick a wrong default (Chat) for HI users whose default is Copilot.
    if (!doctorId) return

    if (allowedSavedView) {
      setQueryParams({
        ...queryParams,
        patientView: allowedSavedView
      })
      return
    }

    setQueryParams({
      ...queryParams,
      patientView: defaultPatientView
    })
  }, [dispatch, queryParams, setQueryParams, defaultPatientView, savedView, allowedViews, doctorId])
}

// View names that are at least one user role's primary view. The
// no-allowedViews-yet branch of the effect uses this to decide between
// "wait for data" and "rewrite to fallback".
const KNOWN_VIEWS = ['copilot', 'chat', 'notes', 'liveCalls']
